import React from 'react';
import get from 'lodash.get';
import { useAuthService } from '@trp-ta-nitro/auth-react';

import { UserReducer, initialState } from '../reducers/userReducer';
import { useApiServices } from './apiServices';
import {
    processConsentData,
    processEngagementStatus,
    getLastCompletedSection,
    getAnalyticsClientType,
    getEngagementTypeCreation,
    getConsentPreselect,
    getIsAdvisorLed,
    getAllClientsEdeliveryConsented
} from './dataProcessors';
import axiosInstance from '../actions/AxiosInstance';
import api from '../actions/EndPoints';
import { groupStatus } from './shared/Content';
import { setMetricsDataStatus } from './shared/Analytics';
import {
    log,
    setLastRequestTime,
    getAnalyticsActiveEngagement,
    getAnalyticsEngagementType,
    getIsRasHH,
    getAnalyticsEngagementStatus,
    getCookie,
} from '../Utils';
import isEmpty from 'lodash.isempty';

/**
 * Main application context API hook that manages user authentication, consent status, and application state
 * Handles session affinity, consent validation, client data processing, and conditional API service calls
 *
 * @param {Object} definedIndexState - Initial application state containing user data and configuration
 * @param {Object} definedIndexState.trpUserData - User data including API key and entitlements
 * @param {Object} definedIndexState.trpUserData.applicationConfig - Application configuration with API key
 * @param {string[]} definedIndexState.trpUserData.userEntitlements - Array of user entitlements
 *
 * @returns {Array} Returns array containing [state, dispatch, setTRPIndexState]
 * @returns {Object} state - Current application state managed by UserReducer
 * @returns {Function} dispatch - Dispatch function for state updates
 * @returns {Function} setTRPIndexState - Function to update TRP index state
 */
export default function AppContextAPI(definedIndexState) {
    const [trpIndexState, setTRPIndexState] = React.useState(definedIndexState);
    const [cancelled, setCancelled] = React.useState(false);
    const authServiceInfo = useAuthService();
    const isCustomerWebView = get(authServiceInfo, 'authType.typeMapping.type', 'external') === 'internal';

    const [state, dispatch] = React.useReducer(UserReducer, {
        ...initialState,
        isCustomerWebView
    });

    const {
        callCacheService,
        callActionService,
        getAvailableDocuments,
        getClientUserStatus,
        getYodleeResponse,
        getAppointmentDetails,
        redirectToTimeTrade,
    } = useApiServices(dispatch);


    /**
     * Handles processing when client has valid (non-expired) consent
     * Dispatches consent status and conditionally calls various API services based on engagement conditions
     * @param {string} apiKey - API key for authentication
     * @param {Object} consentData - Processed consent data from API
     * @param {Object} engagementData - Processed engagement status data
     */
    const handleValidConsent = async (apiKey, consentData, engagementData) => {
        const {
            currentClient, planningGroup, archivedGroupSummary, pendingInvite,
            numberOfClients, activeInvite, hasDocuments, pastEngagements,
            documentsOnFile, isPrimaryClient, rasActiveClient
        } = consentData;

        const {
            status, effectiveDate, isProspStatus, isAddAccStatus,
            prospStatuses, isCEInitiated, getEngagementStatuses
        } = engagementData;

        const consentPreselect = getConsentPreselect(currentClient);
        const allClientsEdeliveryConsented = getAllClientsEdeliveryConsented(planningGroup);
        const lastCompletedSection = getLastCompletedSection(status, isProspStatus, isAddAccStatus);
        const isAdvisorLed = getIsAdvisorLed(isCEInitiated, getEngagementStatuses, planningGroup);
        const checkStatus = planningGroup?.activeEngagement?.engagementStatus?.status;
        const getEDeliveryFlag = currentClient?.activeConsent?.consentEDelivery;

        // Condition checks
        const ifAPNDorAPSCStatus = (checkStatus === groupStatus.APPOINTMENT_NEEDED || checkStatus === groupStatus.APPOINTMENT_SCHEDULED);
        const activeConsent = !isEmpty(currentClient?.activeConsent);
        const isCNGNStatus = planningGroup?.activeEngagement?.engagementStatuses[0]?.status === 'CNGN' && activeConsent;
        const isPreProspCCJoin = isProspStatus && !isPrimaryClient && activeConsent;
        const redirectTTConditions = isPreProspCCJoin || ifAPNDorAPSCStatus || (isProspStatus && isCNGNStatus);
        const isPLDL = planningGroup?.activeEngagement?.engagementStatuses[0].status === 'PLDL';

        setLastRequestTime();

        Promise.resolve(
            dispatch({
                type: 'AUTH_CONSENT_STATUS',
                archivedGroupSummary,
                isPrimary: isPrimaryClient,
                isCustomerWebView,
                lastCompletedSection,
                numberOfClients,
                rasActiveClient,
                isConsented: true,
                isEdeliveryConsented: getEDeliveryFlag === '1',
                allClientsEdeliveryConsented,
                activeInvite,
                currentClientData: currentClient,
                consentPreselect,
                pastEngagements,
                hasDocuments,
                isAdvisorLed,
                hasEnteredPin: false,
                planningGroup,
                pendingInvite,
                documents: documentsOnFile,
                confidenceScoreData: documentsOnFile && planningGroup?.latestReleasedFinancialPlan?.confidenceScores,
                engagementStatus: { status, effectiveDate },
                prospStatuses,
            })
        )
            .then(redirectTTConditions && redirectToTimeTrade(apiKey, checkStatus, pastEngagements))
            .then(redirectTTConditions && getAppointmentDetails(apiKey, planningGroup?.externalId))
            .then((isPLDL || pastEngagements) && getAvailableDocuments(apiKey, planningGroup?.externalId))
            .finally(dispatch({ type: 'LOADING', isLoading: false }));
    };

    /**
     * Handles processing when client consent has expired
     * Sets consent status to false and dispatches after 3 second delay
     * @param {Object} consentData - Processed consent data from API
     * @param {Object} engagementData - Processed engagement status data
     */
    const handleExpiredConsent = (consentData, engagementData) => {
        const {
            currentClient, planningGroup, archivedGroupSummary, pendingInvite,
            numberOfClients, hasDocuments, pastEngagements, documentsOnFile, rasActiveClient, isPrimaryClient
        } = consentData;

        const { status, effectiveDate, prospStatuses } = engagementData;
        const consentPreselect = getConsentPreselect(currentClient);
        const allClientsEdeliveryConsented = getAllClientsEdeliveryConsented(planningGroup);
        const getEDeliveryFlag = currentClient?.activeConsent?.consentEDelivery;

        setTimeout(() => {
            dispatch({
                type: 'AUTH_CONSENT_STATUS',
                isPrimary: isPrimaryClient,
                numberOfClients,
                isLoading: false,
                isCustomerWebView,
                data: {},
                isConsented: false,
                isEdeliveryConsented: getEDeliveryFlag === "1",
                allClientsEdeliveryConsented,
                currentClientData: currentClient,
                lastCompletedSection: null,
                consentPreselect,
                pastEngagements,
                archivedGroupSummary,
                rasActiveClient,
                isAdvisorLed: false,
                hasDocuments,
                hasEnteredPin: false,
                planningGroup,
                pendingInvite,
                documents: documentsOnFile,
                confidenceScoreData: documentsOnFile && planningGroup?.latestReleasedFinancialPlan?.confidenceScores,
                engagementStatus: { status, effectiveDate },
                prospStatuses
            });
        }, 3000);
    };

    /**
     * Handles processing when no current client exists
     * Calls KYC info and conditionally calls other services for prospect clients
     * @param {string} apiKey - API key for authentication
     * @param {Object} consentData - Processed consent data from API
     * @param {Object} engagementData - Processed engagement status data
     */
    const handleNoClient = async (apiKey, consentData, engagementData) => {
        const {
            planningGroup, archivedGroupSummary, pendingInvite,
            numberOfClients, hasDocuments, pastEngagements,
            documentsOnFile, rasActiveClient, isPrimaryClient
        } = consentData;

        const { status, effectiveDate, prospStatuses, isAddAccStatus } = engagementData;
        const consentPreselect = getConsentPreselect(null);
        const allClientsEdeliveryConsented = getAllClientsEdeliveryConsented(planningGroup);
        const lastCompletedSection = getLastCompletedSection(status, false, isAddAccStatus);
        const userData = trpIndexState?.trpUserData;

        setLastRequestTime();

        Promise.resolve()
            .then(() => getClientUserStatus(apiKey))
            .then(() => dispatch({
                type: 'AUTH_CONSENT_STATUS',
                isPrimary: isPrimaryClient,
                numberOfClients,
                isLoading: false,
                isCustomerWebView,
                data: {},
                isConsented: false,
                isEdeliveryConsented: false,
                allClientsEdeliveryConsented,
                consentPreselect,
                archivedGroupSummary,
                lastCompletedSection,
                rasActiveClient,
                planningGroup,
                pendingInvite,
                isAdvisorLed: false,
                hasDocuments,
                hasEnteredPin: false,
                currentClientData: null,
                pastEngagements,
                documents: documentsOnFile,
                confidenceScoreData: documentsOnFile && planningGroup?.latestReleasedFinancialPlan?.confidenceScores,
                engagementStatus: { status, effectiveDate },
                prospStatuses,
                trpUserData: userData
            })).catch(() => {
                dispatch({
                    type: 'AUTH_CONSENT_STATUS',
                    isPrimary: isPrimaryClient,
                    numberOfClients,
                    isLoading: false,
                    isCustomerWebView,
                    data: {},
                    isConsented: false,
                    isEdeliveryConsented: false,
                    allClientsEdeliveryConsented,
                    consentPreselect,
                    archivedGroupSummary,
                    lastCompletedSection,
                    rasActiveClient,
                    planningGroup,
                    pendingInvite,
                    isAdvisorLed: false,
                    hasDocuments,
                    hasEnteredPin: false,
                    currentClientData: null,
                    pastEngagements,
                    documents: documentsOnFile,
                    confidenceScoreData: documentsOnFile && planningGroup?.latestReleasedFinancialPlan?.confidenceScores,
                    engagementStatus: { status, effectiveDate },
                    prospStatuses,
                    trpUserData: userData,
                    userStatusFailure: true
                });
            });
    };

    /**
     * Main consent handler that routes to appropriate handler based on client existence and consent status
     * @param {string} apiKey - API key for authentication
     * @param {Object} consentData - Processed consent data from API
     * @param {Object} engagementData - Processed engagement status data
     */
    const handleClientConsent = async (apiKey, consentData, engagementData) => {
        const { currentClient, planningGroup } = consentData;
        const { status, isCEInitiated, getEngagementStatuses, effectiveDate } = engagementData;
        const isNEWE = planningGroup?.activeEngagement?.engagementStatus?.status === 'NEWE';
        const isAdvisorLed = getIsAdvisorLed(isCEInitiated, getEngagementStatuses, planningGroup);
        const analyticsClientType = getAnalyticsClientType(currentClient);
        const engagementTypeCreation = getEngagementTypeCreation(isAdvisorLed, isCEInitiated);
        const activeEngagement = getAnalyticsActiveEngagement(planningGroup);
        const isRasHH = getIsRasHH(planningGroup, { engagementStatus: status, effectiveDate });

        setMetricsDataStatus(
            isRasHH,
            analyticsClientType,
            getAnalyticsEngagementType(activeEngagement),
            null,
            getAnalyticsEngagementStatus(activeEngagement),
            engagementTypeCreation
        );

        if (currentClient && !isNEWE) {
            callActionService(apiKey);

            if (!cancelled && trpIndexState.trpUserData !== undefined) {
                if (!isEmpty(currentClient?.activeConsent)) {
                    await handleValidConsent(apiKey, consentData, engagementData);
                } else {
                    handleExpiredConsent(consentData, engagementData);
                }
            }
        } else {
            await handleNoClient(apiKey, consentData, engagementData);
        }
    };

    /**
     * Main authorization function that fetches consent data and processes client authorization
     * Sets up initial auth state, calls consent API, and routes to appropriate consent handler
     * @param {string} apiKey - API key for authentication
     */
    const getAuthorization = React.useCallback(
        async (apiKey) => {
            setTRPIndexState(definedIndexState);
            const cookieEnv = getCookie('ii-adv-ui-traffic') ?? 'live';
            const colorCookieEnv = getCookie('adv-svc-traffic') ?? 'live';

            dispatch({
                type: 'AUTH_INITIALIZING',
                isLoading: true,
                isAuthenticated: true,
                trpUserData: trpIndexState?.trpUserData,
                apiKey,
                cookieEnv,
                colorCookieEnv,
                isRegisteredUser: Boolean(
                    trpIndexState?.trpUserData?.userEntitlements?.filter(
                        (user) => user === 'TRP_RET_EXTERNAL_USER_ACCESS'
                    )[0]
                ),
                userEntitlements: trpIndexState?.trpUserData?.userEntitlements,
            });

            try {
                const newConsent = await axiosInstance.get(api.newConsentAPI, {
                    headers: { apikey: `${apiKey}` },
                });

                log(console.log('CONSENT SUMMARY', newConsent.data));

                const consentData = processConsentData(newConsent, trpIndexState);
                const engagementData = processEngagementStatus(consentData.planningGroup);

                await handleClientConsent(apiKey, consentData, engagementData);
            } catch (error) {
                setLastRequestTime();
                if (!cancelled) {
                    dispatch({ type: 'AUTH_FAILURE', isLoading: false, error });

                }
                console.log(error);
            }
        },
        [definedIndexState, trpIndexState, cancelled]
    );

    /**
     * Establishes session affinity with the server and initiates authorization flow
     * Calls cache service, authorization, and Yodlee response regardless of session affinity success/failure
     */
    const establishSessionAffinity = React.useCallback(async () => {
        const apiKey = definedIndexState?.trpUserData?.applicationConfig?.apiKey;

        try {
            const sessionAffinityData = await axiosInstance.get(api.sessionAffinity, {
                headers: { apiKey: `${apiKey}` }
            });
            if (sessionAffinityData) {
                callCacheService(apiKey);
                getAuthorization(apiKey);
                getYodleeResponse(apiKey);
            }
        } catch (error) {
            console.log(error);
            callCacheService(apiKey);
            getAuthorization(apiKey);
            getYodleeResponse(apiKey);
        }
    }, [callCacheService, getAuthorization, definedIndexState]);

    React.useEffect(() => {
        establishSessionAffinity();
        return () => {
            setTRPIndexState(definedIndexState);
            setCancelled(true);
        };
    }, [definedIndexState]);

    return [state, dispatch, setTRPIndexState];
}